export default {
  en: {
    errors: {
      catalogRepository: {
        refresh: 'There was a problem refreshing <b>{catalogRepoName}</b>:<br/>{errorMessage}',
      },
      inventoryResource: {
        fetch: 'Could not find resource <b>{id}</b>',
      },
      pipeline: {
        configuration: {
          update: 'Pipeline configuration - {errorMessage}',
        },
      },
    },
    warning: {
      pipeline: {
        togglePause: 'There was a problem on the pipeline',
      },
    },
    info: {
      envComponent: {
        deletionDelegated: {
          title: 'Deletion scheduled',
          generic:
            'This component is scheduled for removal. A pipeline job will finish deleting it once that job completes successfully. Track progress from your pipeline builds.',
          multiScheduledIntro:
            'Multiple pipeline jobs were started. Scheduled removals finish once each job completes successfully.',
          bodyWithLink:
            'This component is scheduled for removal. Resource check on pipeline <b>{pipelineName}</b> was started. The check will trigger a job responsible for the component deletion. <a href="{buildHref}">See the pipeline overview</a>.',
          bodyNoLink:
            'This component is scheduled for removal. Resource check on pipeline <b>{pipelineName}</b> was started. The check will trigger a job responsible for the component deletion.',
        },
      },
      environment: {
        deletionDelegated: {
          title: 'Deletion scheduled',
          generic:
            'This environment is scheduled for removal. Pipeline jobs will finish deleting it once those jobs complete successfully. Track progress from your pipeline builds.',
          multiScheduledIntro:
            'Multiple pipeline jobs were started. Scheduled removals finish once each job completes successfully.',
          bodyWithLink:
            'This environment is scheduled for removal. Job <b>{jobName}</b> on pipeline <b>{pipelineName}</b> was started (build {buildLabel}, {status}). It will be removed from Cycloid after that job succeeds. <a href="{buildHref}">See the pipeline overview</a>.',
          bodyNoLink:
            'This environment is scheduled for removal. Job <b>{jobName}</b> on pipeline <b>{pipelineName}</b> was started (build {buildLabel}, {status}). It will be removed from Cycloid after that job succeeds.',
        },
      },
      project: {
        deletionDelegated: {
          title: 'Deletion scheduled',
          generic:
            'This project is scheduled for removal. Pipeline jobs will finish deleting it once those jobs complete successfully. Track progress from your pipeline builds.',
          multiScheduledIntro:
            'Multiple pipeline jobs were started. Scheduled removals finish once each job completes successfully.',
          bodyWithLink:
            'This project is scheduled for removal. Job <b>{jobName}</b> on pipeline <b>{pipelineName}</b> was started (build {buildLabel}, {status}). It will be removed from Cycloid after that job succeeds. <a href="{buildHref}">See the pipeline overview</a>.',
          bodyNoLink:
            'This project is scheduled for removal. Job <b>{jobName}</b> on pipeline <b>{pipelineName}</b> was started (build {buildLabel}, {status}). It will be removed from Cycloid after that job succeeds.',
        },
      },
    },
    success: {
      apiKey: {
        created: '@:APIKey added',
        deleted: '<b>{apiKeyName}</b> was successfully deleted',
        reassigned: '@:APIKey <b>{apiKeyName}</b> was reassigned to <b>{owner}</b>',
        updated: '@:APIKey <b>{apiKeyName}</b> updated',
      },
      appearance: {
        created: '<b>{appearanceCanonical}</b> @:appearance created',
        deleted: '<b>{appearanceCanonical}</b> @:appearance deleted',
        updated: '<b>{appearanceCanonical}</b> @:appearance updated',
      },
      auth: {
        verificationEmailSent: 'A verification email has been sent to your inbox',
      },
      buildLeaf: {
        cachesRemoved: 'Caches removed: {cachesRemoved}',
      },
      bulkDeleted: '{deleted} was successfully deleted | {deleted} were successfully deleted',
      catalogRepository: {
        created: '@:CatalogRepository <b>{catalogRepoName}</b> created',
        deleted: '@:CatalogRepository <b>{catalogRepoName}</b> deleted',
        reassigned: '@:CatalogRepository <b>{catalogRepoName}</b> was reassigned to <b>{owner}</b>',
        refreshed: '@:CatalogRepository <b>{catalogRepoName}</b> content was refreshed',
        updated: '@:CatalogRepository <b>{catalogRepoName}</b> updated',
      },
      cloudCostManagement: {
        account: {
          ingesting: 'Cost ingestion has been triggered',
          paused: 'Cost ingestion has been paused',
          unpaused: 'Cost ingestion has been resumed',
        },
        tagMapping: {
          updated: 'Tag mapping was successfully updated',
        },
      },
      configRepository: {
        created: '@:ConfigRepository <b>{configRepoName}</b> created',
        deleted: '@:ConfigRepository <b>{configRepoName}</b> deleted',
        updated: '@:ConfigRepository <b>{configRepoName}</b> updated',
      },
      credential: {
        created: '@:Credential <b>{credentialName}</b> created',
        deleted: '@:Credential <b>{credentialName}</b> deleted',
        reassigned: '@:Credential <b>{credentialName}</b> reassigned to <b>{owner}</b>',
        updated: '@:Credential <b>{credentialName}</b> updated',
      },
      envComponent: {
        created: 'Component successfully created',
        deleted: 'Component successfully deleted',
        updated: 'Component successfully updated',
        configured: 'Component successfully configured',
        started: 'The component was successfully started',
        stopped: 'The component was successfully stopped',
        removeScheduling: 'Scheduling for <b>{componentCanonical}</b> was successfully removed',
      },
      environment: {
        created: 'Environment created successfully',
        updated: 'The environment configuration was successfully updated',
        deleted: 'Environment deleted successfully',
      },
      environmentType: {
        created: 'Environment type created successfully',
        updated: 'Environment type updated successfully',
        deleted: 'Environment type deleted successfully',
      },
      orgCreationNotAllowedMessage: {
        updated: 'The organization message was successfully updated',
      },
      events: {
        preferencesUpdated: 'The event configuration was updated successfully!',
      },
      infraPolicy: {
        created: '@:InfraPolicy <b>{infraPolicyName}</b> created',
        deleted: '@:InfraPolicy <b>{infraPolicyName}</b> deleted',
        reassigned: '@:InfraPolicy <b>{infraPolicyName}</b> reassigned to <b>{owner}</b>',
        updated: '@:InfraPolicy <b>{infraPolicyName}</b> updated',
      },
      KPI: {
        created: 'Widget created',
        updated: 'The <b>{KPIName}</b> widget was successfully updated',
        deleted: 'The <b>{KPIName}</b> widget was successfully deleted',
        favorite: {
          created: 'Widget added to favorites.',
          deleted: 'Widget removed from favorites.',
        },
      },
      notifications: {
        deleted: 'Notification deleted.',
        allDeleted: 'All notifications have been deleted.',
        allMarkedAsRead: 'All notifications have been marked as read.',
      },
      notificationsSettings: {
        updated: 'Notification settings updated.',
      },
      organization: {
        created: 'The organization <b>{orgName}</b> was successfully created',
        deleted: 'The organization <b>{orgName}</b> was successfully deleted',
        updated: 'The organization <b>{orgName}</b> was successfully updated',
        member: {
          updated: '<b>{username}</b> was successfully updated',
        },
        members: {
          invited: 'One person was invited to join <b>{orgName}</b> | <b>{nb}</b> people were invited to join <b>{orgName}</b>',
          invitesResent: 'One invitation was resent, to join <b>{orgName}</b> | <b>{nb}</b> invitations were resent, to join <b>{orgName}</b>',
          unassigned: '{userNames} was unassigned from <b>{orgName}</b> | {userNames} were unassigned from <b>{orgName}</b>',
        },
        subscription: {
          created: 'Congratulations! Your {planName} plan has started',
        },
        namingRules: {
          updated: 'Organization naming rules were updated.',
          deleted: 'Organization naming rules were deleted.',
        },

      },
      sso: {
        updated: '{provider} secret successfully updated',
      },
      oidcMappings: {
        created: 'OIDC group mapping for <b>{groupName}</b> created',
        deleted: 'OIDC group mapping deleted',
        settingsUpdated: 'OIDC settings successfully updated',
      },
      orgLicence: {
        activated: 'Licence successfully activated',
      },
      orgTerraform: {
        created: 'Default Terraform configuration successfully created',
        deleted: 'Default Terraform configuration successfully deleted',
        updated: 'Default Terraform configuration successfully updated',
      },
      pipeline: {
        deleted: 'The <b>{pipelineCanonical}</b> pipeline was successfully deleted',
        toggle: {
          paused: 'Pipeline is paused',
          unpaused: 'Pipeline is running again',
        },
        update: 'Pipeline was updated successfully!',
      },
      profile: {
        updated: 'User profile updated!',
        password: {
          changed: 'Your password has been successfully changed',
        },
        email: {
          verification: {
            sent: 'The verification email has been sent to your inbox',
          },
        },
      },
      project: {
        created: '@:Project <b>{projectName}</b> created',
        deleted: '@:Project <b>{projectName}</b> deleted',
        updated: '@:Project <b>{projectName}</b> updated',
        config: {
          logProviderSaved: 'Logs configuration successfully saved',
          eventProviderSaved: 'Events configuration successfully saved',
          externalBackends: {
            created: 'InfraView configuration successfully created',
            deleted: 'InfraView configuration successfully deleted',
            updated: 'InfraView configuration successfully updated',
          },
          elasticSearch: {
            saved: 'Elastic search logs configuration saved',
          },
          updated: 'The project configuration was updated successfully!',
        },
        env: {
          created: '@:Environment <b>{envCanonical}</b> created',
          deleted: '@:Environment <b>{envCanonical}</b> deleted',
          linked: '@:Environment <b>{envCanonical}</b> added to project',
          unlinked: '@:Environment <b>{envCanonical}</b> removed from project',
        },
        reassigned: '@:Project <b>{projectName}</b> was reassigned to <b>{owner}</b>',
        favorite: {
          created: '<b>{projectName}</b> project added to favorites.',
          deleted: '<b>{projectName}</b> project removed from favorites.',
        },
      },
      resource: {
        created: '<b>@:Resource successfully created</b>',
        deleted: '<b>@:Resource successfully deleted</b>',
        updated: '<b>@:Resource successfully updated</b>',
      },
      formsVersion: {
        updated: '<b>File version successfully updated</b>',
      },
      stacks: {
        deleted: 'Stack <b>{stackName}</b> was successfully deleted',
        updated: 'Stack <b>{stackName}</b> was successfully updated',
        created: '<b>Stack successfully created!</b><br/>You can now create projects with this stack, but you may also start editing it right away.',
      },
      team: {
        created: '@:Team <b>{teamName}</b> created',
        deleted: '@:Team <b>{teamName}</b> deleted',
        reassigned: '@:Team <b>{teamName}</b> reassigned to <b>{owner}</b>',
        updated: '@:Team <b>{teamName}</b> updated',
        members: {
          assigned: '{userNames} was assigned to <b>{teamName}</b> | {userNames} were assigned to <b>{teamName}</b>',
          unassigned: '{userNames} was removed from <b>{teamName}</b> | {userNames} were removed from <b>{teamName}</b>',
        },
      },
      role: {
        duplicated: 'Role successfully duplicated',
        created: 'Role successfully created',
        deleted: 'Role successfully deleted',
        updated: 'Role successfully updated',
      },
      watchRule: {
        created: 'Watch rule successfully created',
        deleted: 'Watch rule successfully deleted',
        updated: 'Watch rule successfully updated',
        watching: 'Watching {projectName} updates.',
        unwatch: 'Stopped watching {projectName} updates.',
      },
    },
  },
  es: {
    errors: {
      catalogRepository: {
        refresh: 'Hubo un problema al actualizar <b>{catalogRepoName}</b>:<br/>{errorMessage}',
      },
      inventoryResource: {
        fetch: 'No se pudo encontrar el recurso <b>{id}</b>',
      },
      pipeline: {
        configuration: {
          update: '¡La configuración del pipeline - {errorMessage}',
        },
      },
    },
    warning: {
      pipeline: {
        togglePause: 'Hubo un problema en la pipeline',
      },
    },
    info: {
      envComponent: {
        deletionDelegated: {
          title: 'Eliminación programada',
          generic:
            'Este componente está programado para eliminarse. Un trabajo del pipeline terminará de borrarlo cuando finalice correctamente. Haz seguimiento desde los builds.',
          multiScheduledIntro:
            'Se iniciaron varios trabajos del pipeline; las eliminaciones programadas terminarán cuando cada trabajo finalice correctamente.',
          bodyWithLink:
            'Este componente está programado para eliminarse. Se inició la comprobación de recurso en la pipeline <b>{pipelineName}</b>. La comprobación disparará un trabajo responsable de eliminar el componente. <a href="{buildHref}">Ver la vista general del pipeline</a>.',
          bodyNoLink:
            'Este componente está programado para eliminarse. Se inició la comprobación de recurso en la pipeline <b>{pipelineName}</b>. La comprobación disparará un trabajo responsable de eliminar el componente.',
        },
      },
      environment: {
        deletionDelegated: {
          title: 'Eliminación programada',
          generic:
            'Este entorno está programado para eliminarse. Los trabajos del pipeline terminarán el proceso cuando finalicen correctamente. Haz seguimiento desde los builds.',
          multiScheduledIntro:
            'Se iniciaron varios trabajos del pipeline; las eliminaciones programadas terminarán cuando cada trabajo finalice correctamente.',
          bodyWithLink:
            'Este entorno está programado para eliminarse. Se inició el trabajo <b>{jobName}</b> en la pipeline <b>{pipelineName}</b> (build {buildLabel}, {status}). Se eliminará de Cycloid cuando ese trabajo finalice correctamente. <a href="{buildHref}">Ver la vista general del pipeline</a>.',
          bodyNoLink:
            'Este entorno está programado para eliminarse. Se inició el trabajo <b>{jobName}</b> en la pipeline <b>{pipelineName}</b> (build {buildLabel}, {status}). Se eliminará de Cycloid cuando ese trabajo finalice correctamente.',
        },
      },
      project: {
        deletionDelegated: {
          title: 'Eliminación programada',
          generic:
            'Este proyecto está programado para eliminarse. Los trabajos del pipeline terminarán el proceso cuando finalicen correctamente. Haz seguimiento desde los builds.',
          multiScheduledIntro:
            'Se iniciaron varios trabajos del pipeline; las eliminaciones programadas terminarán cuando cada trabajo finalice correctamente.',
          bodyWithLink:
            'Este proyecto está programado para eliminarse. Se inició el trabajo <b>{jobName}</b> en la pipeline <b>{pipelineName}</b> (build {buildLabel}, {status}). Se eliminará de Cycloid cuando ese trabajo finalice correctamente. <a href="{buildHref}">Ver la vista general del pipeline</a>.',
          bodyNoLink:
            'Este proyecto está programado para eliminarse. Se inició el trabajo <b>{jobName}</b> en la pipeline <b>{pipelineName}</b> (build {buildLabel}, {status}). Se eliminará de Cycloid cuando ese trabajo finalice correctamente.',
        },
      },
    },
    success: {
      apiKey: {
        created: 'Se agregó la clave de API',
        deleted: '<b>{apiKeyName}</b> se eliminó correctamente',
        reassigned: 'La clave de API <b>{apiKeyName}</b> se reasignó a <b>{owner}</b>',
        updated: 'Se actualizó la clave de API <b>{apiKeyName}</b>',
      },
      appearance: {
        created: '<b>{appearanceCanonical}</b> @:appearance creada',
        deleted: '<b>{appearanceCanonical}</b> @:appearance eliminada',
        updated: '<b>{appearanceCanonical}</b> @:appearance actualizada',
      },
      auth: {
        verificationEmailSent: 'Se ha enviado un correo electrónico de verificación a su bandeja de entrada.',
      },
      buildLeaf: {
        cachesRemoved: 'Caches eliminados: {cachesRemoved}',
      },
      bulkDeleted: 'El {deleted} se eliminó correctamente | {deleted} se eliminaron correctamente',
      catalogRepository: {
        created: '@:CatalogRepository <b>{catalogRepoName}</b> creado',
        deleted: '@:CatalogRepository <b>{catalogRepoName}</b> eliminado',
        reassigned: '@:CatalogRepository <b>{catalogRepoName}</b> reasignado a <b>{owner}</b>',
        refreshed: '@:CatalogRepository <b>{catalogRepoName}</b> el contenido fue actualizado',
        updated: '@:CatalogRepository <b>{catalogRepoName}</b> actualizado',
      },
      cloudCostManagement: {
        account: {
          ingesting: 'La ingesta de costos ha sido activada',
          paused: 'La ingesta de costos ha sido pausada',
          unpaused: 'La ingesta de costos ha sido reanudada',
        },
        tagMapping: {
          updated: 'Mapeo de tags actualizado con éxito',
        },
      },
      configRepository: {
        created: '@:ConfigRepository <b>{configRepoName}</b> creado',
        deleted: '@:ConfigRepository <b>{configRepoName}</b> eliminado',
        updated: '@:ConfigRepository <b>{configRepoName}</b> actualizado',
      },
      credential: {
        created: '@:Credential <b>{credentialName}</b> creado',
        deleted: '@:Credential <b>{credentialName}</b> eliminado',
        reassigned: '@:Credential <b>{credentialName}</b> reasignado a <b>{owner}</b>',
        updated: '@:Credential <b>{credentialName}</b> actualizado',
      },
      envComponent: {
        created: 'Componente creado con éxito',
        deleted: 'Componente eliminado con éxito',
        updated: 'Componente actualizado con éxito',
        configured: 'Componente configurado con éxito',
        started: 'El componente fue iniciado con éxito',
        stopped: 'El componente fue parado con éxito',
        removeScheduling: 'La programación para <b>{componentCanonical}</b> se eliminó con éxito',
      },
      environment: {
        created: 'Entorno creado con éxito',
        updated: 'La configuración del entorno se actualizó correctamente',
        deleted: 'Entorno eliminado con éxito',
      },
      environmentType: {
        created: 'Tipo de entorno creado con éxito',
        updated: 'Tipo de entorno actualizado con éxito',
        deleted: 'Tipo de entorno eliminado con éxito',
      },
      orgCreationNotAllowedMessage: {
        updated: 'El mensaje de la organización se actualizó con éxito',
      },
      events: {
        preferencesUpdated: 'La configuracion de los eventos fué atualizada!',
      },
      infraPolicy: {
        created: '@:InfraPolicy <b>{infraPolicyName}</b> creada',
        deleted: '@:InfraPolicy <b>{infraPolicyName}</b> eliminada',
        reassigned: '@:InfraPolicy <b>{infraPolicyName}</b> reasignado a <b>{owner}</b>',
        updated: '@:InfraPolicy <b>{infraPolicyName}</b> actualizada',
      },
      KPI: {
        created: 'Widget creado',
        updated: 'El widget <b>{KPIName}</b> se actualizó correctamente',
        deleted: 'El widget <b>{KPIName}</b> se eliminó correctamente',
        favorite: {
          created: 'El widget se ha agregado a los favoritos.',
          deleted: 'El widget se ha eliminado de los favoritos.',
        },
      },
      notifications: {
        deleted: 'Notificación eliminada.',
        allDeleted: 'Todas las notificaciones han sido eliminadas.',
        allMarkedAsRead: 'Todas las notificaciones han sido marcadas como leídas.',
      },
      notificationsSettings: {
        updated: 'La configuración de notificaciones se actualizó correctamente.',
      },
      organization: {
        created: 'La organización <b>{orgName}</b> se creó correctamente',
        deleted: 'La organización <b>{orgName}</b> se eliminó correctamente',
        updated: 'La organización <b>{orgName}</b> se actualizó correctamente',
        member: {
          updated: '<b>{username}</b> se actualizó correctamente',
        },
        members: {
          invited: 'Se invitó a una persona a unirse a <b>{orgName}</b> | Se invitó a <b>{nb}</b> personas a unirse a <b>{orgName}</b>',
          invitesResent: 'Una invitación fue reenviada, para unirse a <b>{orgname}</b> | <b> {nb} </b> invitaciones fueron reenviadas, para unirse a <b>{orgname}</b>',
          unassigned: '{userNames} fue removido de <b>{orgName}</b> | {userNames} fueron removidos de <b>{orgName}</b>',
        },
        subscription: {
          created: '¡Enhorabuena! Tu plan de {planName} ha comenzado',
        },
        namingRules: {
          updated: 'Se actualizaron las reglas de nombres de la organización.',
          deleted: 'Se eliminaron las reglas de nombres de la organización.',
        },

      },
      sso: {
        updated: '{provider} clave secreta actualizada correctamente',
      },
      oidcMappings: {
        created: 'Asignación de grupo OIDC para <b>{groupName}</b> creada',
        deleted: 'Asignación de grupo OIDC eliminada',
        settingsUpdated: 'Configuración OIDC actualizada correctamente',
      },
      orgLicence: {
        activated: 'Licencia activada con éxito',
      },
      orgTerraform: {
        created: 'Configuración predeterminada de Terraform creada con éxito',
        deleted: 'La configuración predeterminada de Terraform se eliminó con éxito',
        updated: 'Configuración predeterminada de Terraform actualizada con éxito',
      },
      pipeline: {
        deleted: 'La <b>{pipelineCanonical}</b> pipeline fue eliminada con éxito',
        toggle: {
          paused: 'La pipeline ha sido pausada',
          unpaused: 'La pipeline está en funcionacimiento nuevamente',
        },
        update: '¡La pipeline se ha actualizado con éxito!',
      },
      profile: {
        updated: 'Perfil de usuario actualizado!',
        password: {
          changed: 'Su contraseña ha sido cambiada exitosamente',
        },
        email: {
          verification: {
            sent: 'El correo de verificación ha sido enviado a su bandeja de entrada',
          },
        },
      },
      project: {
        created: '@:Project <b>{projectName}</b> creado',
        deleted: '@:Project <b>{projectName}</b> eliminado',
        updated: '@:Project <b>{projectName}</b> actualizado',
        config: {
          logProviderSaved: 'La configuración de los logs se guardó correctamente',
          eventProviderSaved: 'La configuración de los eventos se guardó correctamente',
          externalBackends: {
            created: 'Configuración de InfraView creada con éxito',
            deleted: `La configuration d'InfraView a été supprimée avec succès`,
            updated: 'La configuración de InfraView se actualizó correctamente',
          },
          elasticSearch: {
            saved: 'Configuración de logs de Elastic Search guardada',
          },
          updated: '¡La configuración del proyecto se ha actualizada!',
        },
        env: {
          created: '@:Environment <b>{envCanonical}</b> creado',
          deleted: '@:Environment <b>{envCanonical}</b> eliminado',
          linked: '@:Environment <b>{envCanonical}</b> agregado al proyecto',
          unlinked: '@:Environment <b>{envCanonical}</b> quitado del proyecto',
        },
        reassigned: '@:Project <b>{projectName}</b> se reasignó a <b>{owner}</b>',
        favorite: {
          created: 'El proyecto <b>{projectName}</b> se ha agregado a los favoritos.',
          deleted: 'El proyecto <b>{projectName}</b> se ha eliminado de los favoritos.',
        },
      },
      resource: {
        created: '<b>@:Resource creado con éxito</b>',
        deleted: '<b>El @:resource eliminada con éxito</b>',
        updated: '<b>El @:resource actualizada con éxito</b>',
      },
      formsVersion: {
        updated: '<b>Versión del file actualizada con éxito</b>',
      },
      stacks: {
        deleted: 'El stack <b>{stackName}</b> ha sido exitosamente eliminado',
        updated: 'El stack <b>{stackName}</b> ha sido exitosamente actualizado',
        created: '<b>¡Stack creado con éxito!</b><br/>Ahora puedes crear proyectos con este stack, pero también puedes comenzar a editarla de inmediato.',
      },
      team: {
        created: '@:Team <b>{teamName}</b> creado',
        deleted: '@:Team <b>{teamName}</b> eliminado',
        reassigned: '@:Team <b>{teamName}</b> reasignado a <b>{owner}</b>',
        updated: '@:Team <b>{teamName}</b> actualizado',
        members: {
          assigned: '{userNames} fue asignado a <b>{teamName}</b> | Se asignaron {userNames} a <b>{teamName}</b>',
          unassigned: '{userNames} se eliminó de <b>{teamName}</b> | {userNames} se eliminaron de <b>{teamName}</b>',
        },
      },
      role: {
        duplicated: 'Rol duplicado con éxito',
        created: 'Rol creado con éxito',
        deleted: 'Rol eliminado con éxito',
        updated: 'Rol actualizado con éxito',
      },
      watchRule: {
        created: 'Regla de monitoreo creada con éxito',
        deleted: 'Regla de monitoreo eliminada con éxito',
        updated: 'Regla de monitoreo actualizada con éxito',
        watching: 'Viendo actualizaciones de {projectName}',
        unwatch: 'Dejar de ver actualizaciones de {projectName}',
      },
    },
  },
  fr: {
    errors: {
      catalogRepository: {
        refresh: `Un problème est survenu lors de l'actualisation de <b>{catalogRepoName}</b>:<br/>{errorMessage}`,
      },
      inventoryResource: {
        fetch: 'Impossible de trouver la ressource <b>{id}</b>',
      },
      pipeline: {
        configuration: {
          update: 'La configuration du pipeline - {errorMessage}',
        },
      },
    },
    warning: {
      pipeline: {
        togglePause: 'Il y a eu un problème avec la pipeline',
      },
    },
    info: {
      envComponent: {
        deletionDelegated: {
          title: 'Suppression planifiée',
          generic:
            'Ce composant est planifié pour suppression. Un job pipeline achèvera la suppression une fois le job terminé avec succès. Suivez l’avancement depuis les builds.',
          multiScheduledIntro:
            'Plusieurs jobs pipeline ont été démarrés ; les suppressions planifiées se terminent lorsque chaque job réussit.',
          bodyWithLink:
            'Ce composant est planifié pour suppression. Une vérification de ressource sur la pipeline <b>{pipelineName}</b> a été démarrée. Elle déclenchera un job chargé de la suppression du composant. <a href="{buildHref}">Voir la vue d’ensemble du pipeline</a>.',
          bodyNoLink:
            'Ce composant est planifié pour suppression. Une vérification de ressource sur la pipeline <b>{pipelineName}</b> a été démarrée. Elle déclenchera un job chargé de la suppression du composant.',
        },
      },
      environment: {
        deletionDelegated: {
          title: 'Suppression planifiée',
          generic:
            'Cet environnement est planifié pour suppression. Les jobs pipeline achèveront la suppression une fois terminés avec succès. Suivez l’avancement depuis les builds.',
          multiScheduledIntro:
            'Plusieurs jobs pipeline ont été démarrés ; les suppressions planifiées se terminent lorsque chaque job réussit.',
          bodyWithLink:
            'Cet environnement est planifié pour suppression. Le job <b>{jobName}</b> sur la pipeline <b>{pipelineName}</b> a été démarré (build {buildLabel}, {status}). Il sera retiré de Cycloid après la réussite de ce job. <a href="{buildHref}">Voir la vue d’ensemble du pipeline</a>.',
          bodyNoLink:
            'Cet environnement est planifié pour suppression. Le job <b>{jobName}</b> sur la pipeline <b>{pipelineName}</b> a été démarré (build {buildLabel}, {status}). Il sera retiré de Cycloid après la réussite de ce job.',
        },
      },
      project: {
        deletionDelegated: {
          title: 'Suppression planifiée',
          generic:
            'Ce projet est planifié pour suppression. Les jobs pipeline achèveront la suppression une fois terminés avec succès. Suivez l’avancement depuis les builds.',
          multiScheduledIntro:
            'Plusieurs jobs pipeline ont été démarrés ; les suppressions planifiées se terminent lorsque chaque job réussit.',
          bodyWithLink:
            'Ce projet est planifié pour suppression. Le job <b>{jobName}</b> sur la pipeline <b>{pipelineName}</b> a été démarré (build {buildLabel}, {status}). Il sera retiré de Cycloid après la réussite de ce job. <a href="{buildHref}">Voir la vue d’ensemble du pipeline</a>.',
          bodyNoLink:
            'Ce projet est planifié pour suppression. Le job <b>{jobName}</b> sur la pipeline <b>{pipelineName}</b> a été démarré (build {buildLabel}, {status}). Il sera retiré de Cycloid après la réussite de ce job.',
        },
      },
    },
    success: {
      apiKey: {
        created: 'Clé API ajoutée',
        deleted: '<b>{apiKeyName}</b> a été supprimée avec succès',
        reassigned: 'Clé API <b>{apiKeyName}</b> a été réaffecté à <b>{owner}</b>',
        updated: 'Clé API <b>{apiKeyName}</b> mise à jour',
      },
      appearance: {
        created: '<b>{appearanceCanonical}</b> @:appearance créé',
        deleted: '<b>{appearanceCanonical}</b> @:appearance supprimé',
        updated: '<b>{appearanceCanonical}</b> @:appearance modifié',
      },
      auth: {
        verificationEmailSent: 'Un email de vérification a été envoyé dans votre boîte de réception',
      },
      buildLeaf: {
        cachesRemoved: 'Caches supprimés: {cachesRemoved}',
      },
      bulkDeleted: '{deleted} a bien été supprimée | Les {deleted} ont bien été supprimées',
      catalogRepository: {
        created: '@:CatalogRepository <b>{catalogRepoName}</b> créé',
        deleted: '@:CatalogRepository <b>{catalogRepoName}</b> supprimée',
        reassigned: '@:CatalogRepository <b>{catalogRepoName}</b> réaffecté à <b>{owner}</b>',
        refreshed: '@:CatalogRepository <b>{catalogRepoName}</b> le contenu a été actualisé',
        updated: '@:CatalogRepository <b>{catalogRepoName}</b> mise à jour',
      },
      cloudCostManagement: {
        account: {
          ingesting: `L'ingestion des coûts a été déclenchée`,
          paused: `L'ingestion des coûts a été mise en pause`,
          unpaused: `L'ingestion des coûts a été reprise`,
        },
        tagMapping: {
          updated: 'Les associations de tags ont été mises à jour avec succès',
        },
      },
      configRepository: {
        created: '@:ConfigRepository <b>{configRepoName}</b> créée',
        deleted: '@:ConfigRepository <b>{configRepoName}</b> supprimée',
        updated: '@:ConfigRepository <b>{configRepoName}</b> modifiée',
      },
      credential: {
        created: `@:Credential <b>{credentialName}</b> créé`,
        deleted: `@:Credential <b>{credentialName}</b> supprimée`,
        reassigned: `@:Credential <b>{credentialName}</b> réaffecté à <b>{owner}</b>`,
        updated: `@:Credential <b>{credentialName}</b> mise à jour`,
      },
      envComponent: {
        created: 'Composant créé avec succès',
        deleted: 'Composant supprimé avec succès',
        updated: 'Composant mis à jour avec succès',
        configured: 'Composant configuré avec succès',
        started: `Le composant a été démarré avec succès`,
        stopped: `Le composant a été éteint avec succès`,
        removeScheduling: 'La planification pour <b>{componentCanonical}</b> a été supprimée avec succès',
      },
      environment: {
        created: `Environnement créé avec succès`,
        updated: `La configuration de l'environnement a été mise à jour avec succès`,
        deleted: `Environnement supprimé avec succès`,
      },
      environmentType: {
        created: `Type d'environnement créé avec succès`,
        updated: `Type d'environnement mis à jour avec succès`,
        deleted: `Type d'environnement supprimé avec succès`,
      },
      orgCreationNotAllowedMessage: {
        updated: `Le message de l'organisation a été mis à jour avec succès`,
      },
      events: {
        preferencesUpdated: 'La configuration des evenements a été mise à jour !',
      },
      infraPolicy: {
        created: '@:InfraPolicy <b>{infraPolicyName}</b> créé',
        deleted: `@:InfraPolicy <b>{infraPolicyName}</b> supprimée`,
        reassigned: '@:InfraPolicy <b>{infraPolicyName}</b> réaffecté à <b>{owner}</b>',
        updated: '@:InfraPolicy <b>{infraPolicyName}</b> actualisée',
      },
      KPI: {
        created: 'Widget créé',
        updated: 'Widget <b>{KPIName}</b> a bien été mis à jour',
        deleted: 'Le widget <b>{KPIName}</b> a bien été supprimé',
        favorite: {
          created: 'Le widget a été ajouté aux favoris',
          deleted: 'Le widget a été retiré des favoris',
        },
      },
      notifications: {
        deleted: 'Notification supprimée',
        allDeleted: 'Toutes les notifications ont été supprimées',
        allMarkedAsRead: 'Toutes les notifications ont été marquées comme lues',
      },
      notificationsSettings: {
        updated: 'La configuration des notifications a été mise à jour avec succès.',
      },
      organization: {
        created: `L'organisation <b>{orgName}</b> a été créée avec succès`,
        deleted: `L'organisation <b>{orgName}</b> a bien été supprimée`,
        updated: `L'organisation <b>{orgName}</b> a été mise à jour avec succès`,
        member: {
          updated: '<b>{username}</b> a été mis à jour avec succès',
        },
        members: {
          invited: 'Une personne a été invitée à rejoindre {orgName} | <b>{nb}</b> personnes ont été invitées à rejoindre <b>{orgName}</b>',
          invitesResent: 'Une invitation a été renvoyée, pour rejoindre <b>{orgname}</b> | <b> {nb} </b> invitations ont été renvoyées, pour rejoindre <b>{orgname}</b>',
          unassigned: '{userNames} a été retiré de <b>{orgName}</b> | {userNames} ont été retirés de <b>{orgName}</b>',
        },
        subscription: {
          created: 'Félicitations! Votre plan {planName} a commencé',
        },
        namingRules: {
          updated: `Les règles de nommage de l'organisation ont été mises à jour.`,
          deleted: `Les règles de nommage de l'organisation ont été supprimées.`,
        },
      },
      sso: {
        updated: '{provider} clé secrète mise à jour avec succès',
      },
      oidcMappings: {
        created: 'Mappage de groupe OIDC pour <b>{groupName}</b> créé',
        deleted: 'Mappage de groupe OIDC supprimé',
        settingsUpdated: 'Paramètres OIDC mis à jour avec succès',
      },
      orgLicence: {
        activated: 'Licence activée avec succès',
      },
      orgTerraform: {
        created: 'La configuration Terraform par défaut a été créée avec succès',
        deleted: 'La configuration Terraform par défaut a été supprimée avec succès',
        updated: 'La configuration Terraform par défaut a été mise à jour avec succès',
      },
      pipeline: {
        deleted: 'La pipeline <b>{pipelineCanonical}</b> a été supprimée avec succès',
        toggle: {
          paused: 'La pipeline est en pause',
          unpaused: 'La pipeline a été réactivée',
        },
        update: 'Pipeline ont été mises à jour!',
      },
      profile: {
        updated: 'Le profil utilisateur a été mis à jour !',
        password: {
          changed: 'Votre mot de passe a été changé avec succès',
        },
        email: {
          verification: {
            sent: `L'email de vérification a été envoyé dans votre boîte de réception`,
          },
        },
      },
      project: {
        created: '@:Project <b>{projectName}</b> créé',
        deleted: '@:Project <b>{projectName}</b> supprimé',
        updated: '@:Project <b>{projectName}</b> mis à jour',
        config: {
          updated: 'La configuration du projet a été mise à jour !',
          logProviderSaved: 'La configuration des logs a été enregistrée avec succès',
          eventProviderSaved: 'La configuration des événements a été enregistrée avec succès',
          externalBackends: {
            created: 'La configuration de InfraView a été créée avec succès',
            deleted: 'La configuración de InfraView se eliminó correctamente',
            updated: `La configuration d'InfraView a été mise à jour avec succès`,
          },
          elasticSearch: {
            saved: 'Configuration des logs Elastic Search enregistrée',
          },
        },
        env: {
          created: '@:Environment <b>{envCanonical}</b> créé',
          deleted: `@:Environment <b>{envCanonical}</b> supprimé`,
          linked: '@:Environment <b>{envCanonical}</b> ajouté au projet',
          unlinked: '@:Environment <b>{envCanonical}</b> retiré du projet',
        },
        reassigned: '@:Project <b>{projectName}</b> a été réaffecté à <b>{owner}</b>',
        favorite: {
          created: 'Le projet <b>{projectName}</b> a été ajouté aux favoris',
          deleted: 'Le projet <b>{projectName}</b> a été retiré des favoris',
        },
      },
      resource: {
        created: '<b>@:Resource créé avec succès</b>',
        deleted: '<b>@:Resource supprimée avec succès</b>',
        updated: '<b>@:Resource mise à jour avec succès</b>',
      },
      formsVersion: {
        updated: '<b>Version du fichier mise à jour avec succès</b>',
      },
      stacks: {
        deleted: 'Le Stack <b>{stackName}</b> a été supprimée avec succès',
        updated: 'Le Stack <b>{stackName}</b> a été mis à jour avec succès',
        created: '<b>Stack créé avec succès !</b><br/>Vous pouvez maintenant créer des projets avec cette stack, mais vous pouvez également commencer à la modifier immédiatement.',
      },
      team: {
        created: '@:Team <b>{teamName}</b> est créée',
        deleted: `@:Team <b>{teamName}</b> supprimée`,
        reassigned: '@:Team <b>{teamName}</b> réaffecté à <b>{owner}</b>',
        updated: '@:Team <b>{teamName}</b> mise à jour',
        members: {
          assigned: '{userNames} a été attribué à <b>{teamName}</b> | {userNames} ont été attribués à <b>{teamName}</b>',
          unassigned: '{userNames} a été supprimé de <b>{teamName}</b> | {userNames} ont été supprimés de <b>{teamName}</b>',
        },
      },
      role: {
        duplicated: 'Rôle dupliqué avec succès',
        created: 'Rôle créé avec succès',
        deleted: 'Rôle supprimé avec succès',
        updated: 'Rôle mis à jour avec succès',
      },
      watchRule: {
        created: 'Règle de notification créée avec succès',
        deleted: 'Règle de notification supprimée avec succès',
        updated: 'Règle de notification mise à jour avec succès',
        watching: 'Suivi des mises à jour de {projectName}',
        unwatch: 'Arrêter de suivre les mises à jour de {projectName}',
      },
    },
  },
}
